(function () {
  'use strict';

  const SIZE_PARAM = 'filter.v.option.size';
  const NORMALIZE_SIZE = true;
  const APPLIED_FORM = '#FacetFiltersForm';
  const MOBILE_FORM_ID = 'FacetFiltersFormMobile';

  let allProducts = [];
  let availMap = {};
  let knownSizes = new Set();
  let soldOutHandles = new Set();
  let currentHandle = null;
  let refreshTimer = null;
  let gridObserver = null;
  let applyBtnObserver = null;

  // Promise jo fetch complete hone par resolve hogi
  let dataReadyPromise = null;
  let dataResolved = false;

  let drawerOpenSnapshot = null;
  let appliedThisSession = false;

  /* ───────────── HELPERS ───────────── */

  function getCollectionHandle() {
    const m = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function norm(val) {
    let s = (val == null ? '' : val).toString().trim();
    if (NORMALIZE_SIZE) s = s.replace(/\s*-\s*\d+\s*$/, '');
    return s.trim().toLowerCase();
  }

  function sizeOptionKey(product) {
    if (!product.options) return null;
    const idx = product.options.findIndex((o) => {
      const name = typeof o === 'object' ? o.name : o;
      return name && name.toLowerCase() === 'size';
    });
    return idx === -1 ? null : `option${idx + 1}`;
  }

  function getItemLabel(item) {
    const labelEl = item.querySelector('.facet-checkbox__text-label');
    if (!labelEl) return '';
    if (!labelEl.dataset.originalLabel) {
      labelEl.dataset.originalLabel = labelEl.textContent.trim();
    }
    return labelEl.dataset.originalLabel;
  }

  function getSelectedSizesFromForm(formSelector) {
    const sizes = [];
    document
      .querySelectorAll(`${formSelector} input[name="${SIZE_PARAM}"]:checked`)
      .forEach((input) => {
        const item = input.closest('li');
        if (!item) return;
        const label = getItemLabel(item);
        if (label) sizes.push(norm(label));
      });
    return [...new Set(sizes)];
  }

  /* ───────────── PARALLEL FETCH ───────────── */

  async function fetchAllProducts(handle) {
    const firstRes = await fetch(`/collections/${handle}/products.json?limit=250&page=1`);
    if (!firstRes.ok) return [];
    const firstData = await firstRes.json();
    const firstBatch = firstData.products || [];

    if (firstBatch.length < 250) return firstBatch;

    const pageNumbers = [];
    for (let p = 2; p <= 20; p++) pageNumbers.push(p);

    const results = await Promise.all(
      pageNumbers.map((p) =>
        fetch(`/collections/${handle}/products.json?limit=250&page=${p}`)
          .then((r) => r.ok ? r.json() : { products: [] })
          .then((d) => d.products || [])
          .catch(() => [])
      )
    );

    let allProds = [...firstBatch];
    for (const batch of results) {
      if (!batch.length) break;
      allProds = allProds.concat(batch);
      if (batch.length < 250) break;
    }

    return allProds;
  }

  /* ───────────── CORE LOGIC ───────────── */

  function buildMaps(products) {
    const avail = {};
    const known = new Set();
    products.forEach((product) => {
      const key = sizeOptionKey(product);
      if (!key || !product.variants) return;
      const countedAvail = new Set();
      product.variants.forEach((v) => {
        const raw = v[key];
        if (!raw) return;
        const base = norm(raw);
        known.add(base);
        if (v.available && !countedAvail.has(base)) {
          countedAvail.add(base);
          avail[base] = (avail[base] || 0) + 1;
        }
      });
    });
    availMap = avail;
    knownSizes = known;
  }

  function buildSoldOutSet(selectedSizes) {
    soldOutHandles = new Set();
    if (!selectedSizes.length) return;
    allProducts.forEach((product) => {
      const key = sizeOptionKey(product);
      if (!key || !product.variants) return;
      const matchesSelected = product.variants.some(
        (v) => v[key] && selectedSizes.includes(norm(v[key]))
      );
      if (!matchesSelected) return;
      const hasAvailable = product.variants.some(
        (v) => v.available && v[key] && selectedSizes.includes(norm(v[key]))
      );
      if (!hasAvailable) soldOutHandles.add(product.handle);
    });
  }

  // Counts ko hide karo jab tak data ready na ho
  function hideFilterCounts() {
    const items = document.querySelectorAll('li.facets__item, li.mobile-facets__item');
    items.forEach((item) => {
      const input = item.querySelector(`input[name="${SIZE_PARAM}"]`);
      if (!input) return;
      const textEl = item.querySelector('.facet-checkbox__text');
      if (!textEl) return;
      const label = getItemLabel(item);
      if (!label) return;
      // Count hata do, sirf label dikhao
      textEl.innerHTML = `<span class="facet-checkbox__text-label" data-original-label="${label}">${label}</span>`;
    });
  }

  function updateFilterCounts() {
    const items = document.querySelectorAll('li.facets__item, li.mobile-facets__item');
    items.forEach((item) => {
      const input = item.querySelector(`input[name="${SIZE_PARAM}"]`);
      if (!input) return;
      const label = getItemLabel(item);
      if (!label) return;
      const base = norm(label);
      if (!knownSizes.has(base)) { item.style.display = ''; return; }
      const count = availMap[base] || 0;
      const isActive = input.checked;
      if (count === 0 && !isActive) { item.style.display = 'none'; return; }
      item.style.display = '';
      const textEl = item.querySelector('.facet-checkbox__text');
      if (!textEl) return;

      // FIX (iOS flicker): only touch the DOM if the text actually changed.
      // Previously this always rewrote innerHTML, which — combined with the
      // MutationObserver watching this same container — caused a self
      // triggering refresh loop (see observeGrid/refresh fix below). Even
      // with that loop fixed, avoiding needless innerHTML writes prevents
      // unnecessary reflow/repaint on iOS Safari.
      const newLabel = `${label} (${count})`;
      if (textEl.dataset.faRendered === newLabel) return;
      textEl.dataset.faRendered = newLabel;
      textEl.innerHTML = `<span class="facet-checkbox__text-label" data-original-label="${label}">${label}</span> (${count})`;
    });
  }

  function applyCardVisibility() {
    const cards = document.querySelectorAll(
      '.product-card-wrapper, .card-wrapper, li.grid__item, .grid__item'
    );
    cards.forEach((card) => {
      const link = card.querySelector('a[href*="/products/"]');
      if (!link) return;
      const m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      const handle = m ? m[1] : null;
      if (!handle) return;
      const shouldHide = soldOutHandles.has(handle);
      const newDisplay = shouldHide ? 'none' : '';
      // FIX (iOS flicker): skip the write if nothing changes.
      if (card.style.display === newDisplay) return;
      card.style.display = newDisplay;
    });
  }

  function injectStyles() {
    if (document.getElementById('fa-count-style')) return;
    const style = document.createElement('style');
    style.id = 'fa-count-style';
    style.textContent =
      '#ProductCountDesktop.loading,#ProductCount.loading{visibility:visible !important;}' +
      '.product-count .loading__spinner,.product-count .spinner,.product-count [class*="spinner"]{display:none !important;}';
    (document.head || document.documentElement).appendChild(style);
  }
  injectStyles();

  function killSpinners() {
    document.querySelectorAll('#ProductCountDesktop, #ProductCount')
      .forEach((el) => el.classList.remove('loading'));
    document.querySelectorAll(
      '.product-count [class*="spinner"], .product-count svg, .loading-spinner, .loading__spinner'
    ).forEach((s) => { s.style.display = 'none'; s.classList.add('hidden'); });
    document.querySelectorAll('.product-count')
      .forEach((p) => p.classList.remove('loading', 'is-loading'));
  }

  function getActiveSelections() {
    const sel = {};
    document
      .querySelectorAll(`${APPLIED_FORM} input[name^="filter."]:checked`)
      .forEach((input) => {
        const name = input.name;
        const item = input.closest('li');
        if (!item) return;
        const labelEl = item.querySelector('.facet-checkbox__text-label');
        if (!labelEl) return;
        const label = labelEl.dataset.originalLabel || labelEl.textContent.trim();
        if (!label) return;
        sel[name] = sel[name] || [];
        if (!sel[name].includes(label)) sel[name].push(label);
      });
    return sel;
  }

  function getPriceRange() {
    const p = new URLSearchParams(location.search);
    const gte = parseFloat(p.get('filter.v.price.gte'));
    const lte = parseFloat(p.get('filter.v.price.lte'));
    return { gte: isNaN(gte) ? null : gte, lte: isNaN(lte) ? null : lte };
  }

  function hasUnsupportedFilter(sel) {
    return Object.keys(sel).some((n) => n.indexOf('filter.p.m.') === 0);
  }

  function optionIndexMap(product) {
    const map = {};
    (product.options || []).forEach((o, i) => {
      const nm = typeof o === 'object' ? o.name : o;
      if (nm) map[nm.toLowerCase()] = 'option' + (i + 1);
    });
    return map;
  }

  function productMatches(product, sel, price) {
    if (sel['filter.p.product_type'] &&
      !sel['filter.p.product_type'].map(s => s.toLowerCase())
        .includes((product.product_type || '').toLowerCase())) return false;
    if (sel['filter.p.vendor'] &&
      !sel['filter.p.vendor'].map(s => s.toLowerCase())
        .includes((product.vendor || '').toLowerCase())) return false;
    if (sel['filter.p.tag']) {
      const ptags = (product.tags || []).map((t) => t.toLowerCase());
      if (!sel['filter.p.tag'].map(s => s.toLowerCase()).some((t) => ptags.includes(t)))
        return false;
    }
    const optMap = optionIndexMap(product);
    const variantOptionSel = Object.keys(sel)
      .filter((n) => n.indexOf('filter.v.option.') === 0)
      .map((n) => ({ key: optMap[n.slice(16).toLowerCase()], values: sel[n].map(norm) }));
    return (product.variants || []).some((v) => {
      if (!v.available) return false;
      if (price.gte != null && parseFloat(v.price) < price.gte) return false;
      if (price.lte != null && parseFloat(v.price) > price.lte) return false;
      for (const o of variantOptionSel) {
        if (!o.key) return false;
        const vv = v[o.key];
        if (!vv || !o.values.includes(norm(vv))) return false;
      }
      return true;
    });
  }

  function getTotalCount(sel, price) {
    const anyFilter = Object.keys(sel).length > 0 || price.gte != null || price.lte != null;
    if (!anyFilter) return allProducts.length;
    let count = 0;
    allProducts.forEach((p) => { if (productMatches(p, sel, price)) count++; });
    return count;
  }

  function updateProductCountUI() {
    const sel = getActiveSelections();
    const price = getPriceRange();
    if (!hasUnsupportedFilter(sel)) {
      const n = getTotalCount(sel, price);
      const text = n === 1 ? '1 product' : `${n} products`;
      document.querySelectorAll('#ProductCountDesktop, #ProductCount')
        .forEach((el) => { if (el.textContent !== text) el.textContent = text; });
      document.querySelectorAll('.mobile-facets__count')
        .forEach((el) => { if (el.textContent !== text) el.textContent = text; });
    }
    killSpinners();
  }

  function refresh() {
    if (!allProducts.length) return;

    // FIX (iOS flicker): the gridObserver watches #MainContent/main/body with
    // subtree:true, and updateFilterCounts()/applyCardVisibility()/
    // updateProductCountUI() below all mutate elements *inside* that same
    // container. That meant every refresh() re-triggered the observer,
    // which scheduled another refresh(), which mutated the DOM again,
    // forever — a self-triggering feedback loop. It was subtle on
    // desktop Chrome but caused a visible flicker on iOS Safari because
    // of how it batches/repaints reflows. Disconnecting the observer while
    // we write, then reconnecting after, breaks the loop.
    if (gridObserver) gridObserver.disconnect();

    const selectedSizes = getSelectedSizesFromForm(APPLIED_FORM);
    buildSoldOutSet(selectedSizes);
    updateFilterCounts();
    applyCardVisibility();
    updateProductCountUI();

    observeGrid();
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 80);
  }

  /* ───────────── BOOTSTRAP ───────────── */

  async function init() {
    const handle = getCollectionHandle();
    if (!handle) return;

    if (handle !== currentHandle || !allProducts.length) {
      currentHandle = handle;
      dataResolved = false;

      // PAGE LOAD PE TURANT: counts hide karo (Shopify ke galat counts mat dikhne do)
      hideFilterCounts();

      // Background mein fetch shuru — Promise save karo
      dataReadyPromise = fetchAllProducts(handle).then((products) => {
        allProducts = products;
        if (!allProducts.length) return;
        buildMaps(allProducts);
        dataResolved = true;
        // Data ready — ab sahi counts dikhao
        refresh();
        observeGrid();
      });

    } else {
      // Data already loaded hai
      refresh();
      observeGrid();
    }
  }

  // Jab filter open ho — agar data ready hai seedha dikhao,
  // agar nahi toh wait karo phir dikhao
  async function onFilterOpen() {
    if (dataResolved) {
      refresh();
      return;
    }
    if (dataReadyPromise) {
      await dataReadyPromise;
      refresh();
    }
  }

  function observeGrid() {
    const container =
      document.querySelector('#MainContent') ||
      document.querySelector('main') ||
      document.body;
    if (!container) return;

    // FIX (iOS flicker): reuse the same observer instance instead of
    // creating a new one every call — just disconnect + re-observe.
    if (!gridObserver) {
      gridObserver = new MutationObserver(scheduleRefresh);
    } else {
      gridObserver.disconnect();
    }
    gridObserver.observe(container, { childList: true, subtree: true });
  }

  /* ───────────── FILTER OPEN DETECT ───────────── */

  function bindFilterOpenEvents() {
    // Desktop: details element open hone par
    document.addEventListener('click', (e) => {
      const summary = e.target.closest('.facets__summary, .mobile-facets__summary, .mobile-facets__open-wrapper');
      if (summary) {
        onFilterOpen();
      }
    }, true);

    // Mobile drawer open
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('.mobile-facets__open');
      if (opener) {
        onFilterOpen();
      }
    }, true);
  }

  /* ───────────── MOBILE FILTER ───────────── */

  function getMobileApplyButtons() {
    return document.querySelectorAll(
      '#' + MOBILE_FORM_ID + ' .apply-button, .mobile-facets__wrapper .apply-button'
    );
  }

  function mobileHasSelection() {
    const form = document.getElementById(MOBILE_FORM_ID);
    if (!form) return false;
    return !!form.querySelector('input[type="checkbox"]:checked');
  }

  function urlHasFilters() {
    const p = new URLSearchParams(location.search);
    for (const k of p.keys()) {
      if (k.indexOf('filter.') === 0) return true;
    }
    return false;
  }

  function shouldEnableApply() {
    return mobileHasSelection() || urlHasFilters();
  }

  function setApplyState() {
    const enable = shouldEnableApply();
    getMobileApplyButtons().forEach((btn) => {
      if (enable) {
        btn.disabled = false;
        btn.removeAttribute('disabled');
        btn.classList.remove('disabled');
      } else {
        btn.disabled = true;
        btn.setAttribute('disabled', 'disabled');
        btn.classList.add('disabled');
      }
    });
  }

  function startApplyObserver() {
    if (applyBtnObserver) applyBtnObserver.disconnect();
    const root = document.querySelector('.mobile-facets__wrapper') ||
      document.querySelector('.mobile-facets__inner') ||
      document.body;
    if (!root) return;

    applyBtnObserver = new MutationObserver((muts) => {
      if (!shouldEnableApply()) return;
      for (const m of muts) {
        const t = m.target;
        if (
          t.nodeType === 1 &&
          t.classList &&
          t.classList.contains('apply-button') &&
          (t.disabled || t.classList.contains('disabled'))
        ) {
          t.disabled = false;
          t.removeAttribute('disabled');
          t.classList.remove('disabled');
        }
      }
    });
    applyBtnObserver.observe(root, {
      attributes: true,
      subtree: true,
      attributeFilter: ['disabled', 'class'],
    });
  }

  function takeDrawerSnapshot() {
    const form = document.getElementById(MOBILE_FORM_ID);
    if (!form) { drawerOpenSnapshot = null; return; }
    drawerOpenSnapshot = {};
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      drawerOpenSnapshot[input.name + '::' + input.value] = input.checked;
    });
  }

  function revertToSnapshot() {
    if (!drawerOpenSnapshot) { syncMobileToURL(); return; }
    const form = document.getElementById(MOBILE_FORM_ID);
    if (!form) return;
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const key = input.name + '::' + input.value;
      if (key in drawerOpenSnapshot) input.checked = drawerOpenSnapshot[key];
    });
    setApplyState();
  }

  function syncMobileToURL() {
    const form = document.getElementById(MOBILE_FORM_ID);
    if (!form) return;
    const appliedVals = new URLSearchParams(location.search)
      .getAll(SIZE_PARAM).map(norm);
    form.querySelectorAll(`input[name="${SIZE_PARAM}"]`).forEach((input) => {
      input.checked = appliedVals.includes(norm(input.value || ''));
    });
    setApplyState();
  }

  function applyMobileFilter() {
    const mobileForm = document.getElementById(MOBILE_FORM_ID);
    const desktopForm = document.querySelector(APPLIED_FORM);
    if (!mobileForm || !desktopForm) return;

    // Checkbox filters (size, sleeve, color, etc.) — sab sync karo, sirf size nahi
    const mobileCheckedKeys = new Set();
    mobileForm
      .querySelectorAll('input[type="checkbox"][name^="filter."]')
      .forEach((i) => {
        if (i.checked) mobileCheckedKeys.add(i.name + '::' + i.value);
      });

    desktopForm
      .querySelectorAll('input[type="checkbox"][name^="filter."]')
      .forEach((i) => {
        i.checked = mobileCheckedKeys.has(i.name + '::' + i.value);
      });

    // Range / hidden price inputs bhi sync karo (agar mobile me price slider hai)
    mobileForm
      .querySelectorAll('input[name^="filter."]:not([type="checkbox"])')
      .forEach((mi) => {
        const di = desktopForm.querySelector(`input[name="${mi.name}"]:not([type="checkbox"])`);
        if (di) di.value = mi.value;
      });

    appliedThisSession = true;

    const desktopFacetEl = desktopForm.closest('facet-filters-form');
    if (desktopFacetEl) {
      desktopFacetEl.dispatchEvent(new Event('input', { bubbles: true }));
    }
    desktopForm.dispatchEvent(new Event('input', { bubbles: true }));

    setTimeout(() => {
      const details = document.querySelector('.mobile-facets__disclosure');
      if (details && details.open) {
        const sum = details.querySelector('summary');
        if (sum) sum.click();
      }
    }, 100);
}

  function setupMobileFilter() {
    setTimeout(() => {
      syncMobileToURL();
      setApplyState();
      startApplyObserver();
    }, 500);

    // function onMobileChange(e) {
    //   const t = e.target;
    //   if (!t || !t.closest) return;
    //   if (!t.closest('#' + MOBILE_FORM_ID)) return;
    //   e.stopImmediatePropagation();
    //   if (t.matches('input[type="checkbox"], input[type="range"], select')) {
    //     setApplyState();
    //   }
    // }
    // document.addEventListener('input', onMobileChange, true);
    // document.addEventListener('change', onMobileChange, true);

    document.addEventListener('click', (e) => {

      const btn = e.target.closest('button');
      if (
        btn &&
        btn.classList.contains('apply-button') &&
        btn.closest('#' + MOBILE_FORM_ID) &&
        !btn.disabled
      ) {
        e.preventDefault();
        e.stopImmediatePropagation();
        applyMobileFilter();
        return;
      }

      if (e.target.closest('.mobile-facets__close_btn')) {
        if (!appliedThisSession) setTimeout(revertToSnapshot, 50);
        appliedThisSession = false;
        return;
      }

      const opener = e.target.closest('.mobile-facets__open-wrapper');
      if (opener) {
        const details = opener.closest('details');
        if (details) {
          if (details.open) {
            if (!appliedThisSession) setTimeout(revertToSnapshot, 50);
            appliedThisSession = false;
          } else {
            setTimeout(() => {
              takeDrawerSnapshot();
              setApplyState();
            }, 80);
          }
        }
      }

      if (e.target.closest('facet-remove, .mobile-facets__clear')) {
        setTimeout(() => {
          syncMobileToURL();
          drawerOpenSnapshot = null;
        }, 800);
      }

    }, true);
  }

  /* ───────────── DESKTOP FALLBACK ───────────── */

  function bindInteractionFallback() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('facet-remove, .active-facets__button')) {
        setTimeout(refresh, 400);
        setTimeout(refresh, 900);
      }
    }, true);
    document.addEventListener('change', (e) => {
      if (
        e.target.matches(`input[name="${SIZE_PARAM}"]`) &&
        e.target.closest(APPLIED_FORM)
      ) {
        setTimeout(refresh, 300);
      }
    });
  }

  /* ───────────── BOOT ───────────── */

  function boot() {
    if (!window.location.pathname.includes('/collections/')) return;
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupMobileFilter();
      bindInteractionFallback();
      bindFilterOpenEvents();
      boot();
    });
  } else {
    setupMobileFilter();
    bindInteractionFallback();
    bindFilterOpenEvents();
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

})();